// Step 2: name and version.
//
// The name is checked against the server as it is typed, because it is not just a
// label — it becomes the container name too, and discovering a clash at deploy
// time means unwinding a half-built worker.

import { useQuery } from "@tanstack/react-query";
import { FieldDescription } from "@veil/ui/components/field";
import { Spinner } from "@veil/ui/components/spinner";
import { useDebouncedValue } from "@veil/ui/hooks/use-debounced-value";
import CircleCheck from "lucide-react/dist/esm/icons/circle-check";
import CircleX from "lucide-react/dist/esm/icons/circle-x";
import type { UseFormReturn } from "react-hook-form";
import { SelectField, TextField } from "@/components/form/text-field";
import { api } from "@/lib/api";
import type { DeployWorkerValue } from "../schema";
import type { WorkerImageVersions } from "../use-worker-images";

interface NameCheck {
	available: boolean;
	reason: string | null;
}

/**
 * Ask the server whether a name is free. Debounced so typing does not produce a
 * request per keystroke, and skipped while the field is locally invalid — there
 * is nothing to check about a malformed name.
 */
function useNameAvailability(name: string, enabled: boolean) {
	const debounced = useDebouncedValue(name, 400);
	return useQuery({
		queryKey: ["worker-name-available", debounced],
		enabled: enabled && debounced.length > 0,
		queryFn: async (): Promise<NameCheck> => {
			const { data } = await api.api.workers["name-available"].get({
				query: { name: debounced },
			});
			if (!data || data.success !== true) {
				return { available: true, reason: null };
			}
			return { available: data.available, reason: data.reason };
		},
		staleTime: 15_000,
	});
}

export function ConfigureStep({
	form,
	images,
}: {
	form: UseFormReturn<DeployWorkerValue>;
	images: WorkerImageVersions | undefined;
}) {
	const name = form.watch("name");
	const nameError = form.formState.errors.name;
	const check = useNameAvailability(name, !nameError);

	// "latest" is what most operators want, but it should say what it resolves to.
	const latestLabel = images?.latest ? `latest (${images.latest})` : "latest";
	const versionOptions = [
		{ value: "latest", label: latestLabel },
		...(images?.versions ?? []).map((v) => ({ value: v, label: v })),
	];

	return (
		<div className="grid gap-4">
			<div>
				<TextField
					id="deploy-name"
					label="Worker name"
					placeholder="worker-1"
					autoComplete="off"
					spellCheck={false}
					className="max-w-sm"
					error={nameError}
					description={
						<>
							Lowercase letters, digits and dashes. Also used for the container,
							as <code>veil-worker-{name || "<name>"}</code>.
						</>
					}
					{...form.register("name")}
				/>
				<NameStatus
					pending={check.isFetching}
					result={check.data}
					show={!nameError && name.length > 0}
				/>
			</div>

			<SelectField
				id="deploy-version"
				label="Version"
				className="max-w-sm"
				error={form.formState.errors.imageTag}
				options={versionOptions}
				description={
					images?.error
						? `Could not reach the registry to list versions (${images.error}). You can still deploy the default.`
						: "Pin a version for a reproducible deploy, or take the latest."
				}
				{...form.register("imageTag")}
			/>
		</div>
	);
}

function NameStatus({
	pending,
	result,
	show,
}: {
	pending: boolean;
	result: NameCheck | undefined;
	show: boolean;
}) {
	if (!show) return null;
	if (pending) {
		return (
			<FieldDescription className="mt-1 flex items-center gap-1">
				<Spinner className="size-3" /> Checking availability…
			</FieldDescription>
		);
	}
	if (!result) return null;
	if (result.available) {
		return (
			<FieldDescription className="mt-1 flex items-center gap-1 text-success">
				<CircleCheck className="size-3" aria-hidden="true" /> Name is available
			</FieldDescription>
		);
	}
	return (
		// role=alert so the conflict is announced, not just coloured.
		<FieldDescription
			role="alert"
			className="mt-1 flex items-center gap-1 text-destructive"
		>
			<CircleX className="size-3" aria-hidden="true" />
			{result.reason ?? "That name is taken"}
		</FieldDescription>
	);
}
